/* TestRuleRenderer.js */

"use strict";
const _ = require("lodash");
const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
const TextAreaControl = require("@gluenetworks/steve_ui_core").Control.Textfield.Textarea;
const ComboboxControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single.ComboBox;
const AuditPolicyResultTree = require("../../../../../../AuditPolicyResultTree");

const FILTER_LABELS = {
    "<FullConfig>": "Full Configuration",
    "<FilteredConfig>": "Filtered Configuration",
    "<Empty>": "Empty",
    "<All>": "All"
};

class TestRuleRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("validate-rule-gl"); //TODO Setup the root Element
        this._rootElem.component = this._model;

        this._testDataRegExComponent = new TextAreaControl({
            darkTheme: true
        });
        this._DevicePropertiesRegExComponent = new TextAreaControl({
            darkTheme: true
        });

        this._filterRegExComponent = new TextAreaControl({
            darkTheme: true,
            disabled: true
        });

        this._evalResults = {};
        this._devicePropsData = {};
        // this._model._parentRenderer._ifAdvancedFilter._columnsConfig.forEach(i => Object.assign(this._devicePropsData, {[i.name]: ''}));

        // this._DevicePropertiesRegExComponent.setValue(JSON.stringify(this._devicePropsData, undefined, 4));

        this._conditionsList = new ComboboxControl({
            darkTheme: true,
            placeholder: "Condition",
            showFilter: false
        });
        this._conditionsList.setOptions([
            {
                value: "then",
                label: "THEN"
            },
            {
                value: "else",
                label: "ELSE",
                disabled: this._model._parentRenderer._filterType.getValue() === "IF_THEN_ELSE" ? false : true
            }
        ]);
        this._conditionsList.setValue("then", false);

        this._setDeviceProps("then");

        this._filterList = new ComboboxControl({
            darkTheme: true,
            placeholder: "",
            showFilter: false
        });
        this._filterList.setDisabled(true);

        this._textAreaControl = new TextAreaControl({
            darkTheme: true,
            disabled: true
        });

        this._cancelButton = new ButtonControl({
            label: "Cancel",
            darkTheme: true
        });

        this._testButton = new ButtonControl({
            label: "Test",
            darkTheme: true,
            theme: "additive",
            disabled: true
        });

        const userData = this._model._getUserData();
        if (userData.configLines) {
            this._testDataRegExComponent.setValue(userData.configLines);
            this._testButton.setDisabled(false);
        }

        this._errorContainer = document.createElement("span");
        this._errorContainer.classList.add("error-span");

        this._titleComponent = document.createElement("h2");
        this._titleComponent.innerText = this._model.titleText;

        this._buttonContainer = document.createElement("button-container");

        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _registerListeners() {
        this._testDataRegExComponent.on("UserValue", (this._onTestDataChange = this._onTestDataChange.bind(this)));
        this._DevicePropertiesRegExComponent.on("UserValue", (this._onTestDataChange = this._onTestDataChange.bind(this)));
        this._conditionsList.on("UserValue", (this._onConditionsListChange = this._onConditionsListChange.bind(this)));
        this._filterList.on("UserValue", (this._onFiltersChange = this._onFiltersChange.bind(this)));

        this._cancelButton.on("UserClick", (this._onCancelButtonClick = this._onCancelButtonClick.bind(this)));
        this._testButton.on("UserClick", (this._onSaveButtonClick = this._onSaveButtonClick.bind(this)));

        this._model.on("Results", (this._onResults = this._onResults.bind(this)));
        this._model.on("Error", (this._onError = this._onError.bind(this)));

        this._resizeObserver = new ResizeObserver((this._onResize = this._onResize.bind(this)));
        this._resizeObserver.observe(this._testDataRegExComponent._renderer._rootElem);
        this._resizeObserver.observe(this._DevicePropertiesRegExComponent._renderer._rootElem);
        this._resizeObserver.observe(this._filterRegExComponent._renderer._rootElem);
        this._resizeObserver.observe(this._textAreaControl._renderer._rootElem);
    }

    _unregisterListeners() {
        this._testDataRegExComponent.removeAllListeners("UserValue");
        this._DevicePropertiesRegExComponent.removeAllListeners("UserValue");
        this._conditionsList.removeAllListeners("UserValue");
        this._filterList.removeAllListeners("UserValue");
        this._cancelButton.removeAllListeners("UserClick");
        this._testButton.removeAllListeners("UserClick");

        this._resizeObserver.disconnect();
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        let promArr = [
            this._testDataRegExComponent.shutdown(),
            this._DevicePropertiesRegExComponent.shutdown(),
            this._conditionsList.shutdown(),
            this._cancelButton.shutdown(),
            this._testButton.shutdown()
        ];

        return Promise.all(promArr).then(
            () => {
                this._evalResults = undefined;
                this._testDataRegExComponent = undefined;
                this._cancelButton = undefined;
                this._testButton = undefined;
                this._buttonContainer.remove();
                this._buttonContainer = undefined;
                this._titleComponent.remove();
                this._titleComponent = undefined;
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
                this._DevicePropertiesRegExComponent = undefined;
                this._conditionsList = undefined;
                this._resizeObserver = undefined;
            },
            (err) => {
                this._model.logger.warn(err, "CreateQuestionRenderer.shutdown() error");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return new Promise((resolve /*,reject*/) => {
            if (!this._rootElem.hasChildNodes()) {
                this._rootElem.appendChild(this._titleComponent);
                this._createTestVeilLayout();

                const userData = this._model._getUserData();
                if (userData.configLines) {
                    if (userData.deviceProps) {
                        this._updateDeviceProps();
                    }
                    this._model.updateWorkInput();
                }
            }

            resolve();
        });
    }

    _updateDeviceProps() {
        const userData = this._model._getUserData(),
            deviceProps = this._model._getDevicePropsFromField();
        if (userData.deviceProps) {
            _.keys(deviceProps).forEach((item) => {
                if (userData.deviceProps[item]) {
                    deviceProps[item] = userData.deviceProps[item];
                }
            });
            this._DevicePropertiesRegExComponent.setValue(JSON.stringify(deviceProps, undefined, 4));
        }
    }

    async _createTestVeilLayout() {
        this._rootElem.appendChild(this._renderVeil());
        this._rootElem.appendChild(this._errorContainer);
        this._rootElem.appendChild(this._buttonContainer);
        this._buttonContainer.appendChild(this._cancelButton.renderer.rootElement);
        this._buttonContainer.appendChild(this._testButton.renderer.rootElement);
    }

    _renderVeil() {
        const sectionMain = document.createElement("div");

        sectionMain.appendChild(this.createSection1());
        sectionMain.appendChild(this.createSection2());

        return sectionMain;
    }

    createSection1() {
        const section1 = this.createSection("Source", true, "source-section"),
            leftSection = document.createElement("div"),
            rightSection = document.createElement("div"),
            rightRegExContainer = document.createElement("div");

        rightRegExContainer.classList.add("right-regex-container");
        rightRegExContainer.appendChild(this._DevicePropertiesRegExComponent._renderer._rootElem);

        leftSection.classList.add("left-section");
        rightSection.classList.add("right-section");

        leftSection.appendChild(this.createLabel("Test Data"));
        leftSection.appendChild(this._testDataRegExComponent._renderer._rootElem);
        rightSection.appendChild(this.createLabel("Device Properties"));
        rightSection.appendChild(rightRegExContainer);

        if (this._model._parentRenderer._filterType.getValue() !== "SIMPLE") {
            rightSection.appendChild(this.createLabel("Condition", "condition-label"));
            rightSection.appendChild(this._conditionsList._renderer.getRootElement());
        }

        section1.appendChild(leftSection);
        section1.appendChild(rightSection);

        return section1;
    }

    createSection2() {
        const section2 = this.createSection("Results", true, "result-section"),
            leftSection = document.createElement("div"),
            rightSection = document.createElement("div"),
            filtersListContainer = document.createElement("div");
        filtersListContainer.classList.add("filters-list-container");

        leftSection.classList.add("left-section");
        rightSection.classList.add("right-section");

        leftSection.appendChild(this.createLabel("Filter"));
        leftSection.appendChild(this._filterRegExComponent._renderer._rootElem);

        rightSection.appendChild(this.createLabel("Rule"));
        rightSection.appendChild(this._textAreaControl._renderer.getRootElement());

        filtersListContainer.appendChild(this._filterList._renderer._rootElem);
        section2.appendChild(filtersListContainer);
        section2.appendChild(leftSection);
        section2.appendChild(rightSection);

        return section2;
    }

    createSection(title = "", border = true, classname = "") {
        const parentElem = document.createElement("div"),
            titleElem = document.createElement("label");
        parentElem.classList.add("bordered-section-parent");
        if (classname) {
            parentElem.classList.add(classname);
        }
        parentElem.classList.add(border ? "bordered" : "no-border");
        titleElem.classList.add("section-title");
        titleElem.innerText = title;

        parentElem.appendChild(titleElem);

        return parentElem;
    }

    createLabel(title = "", classname = "") {
        const label = document.createElement("label");
        if (classname) {
            label.classList.add(classname);
        }
        label.innerText = title;
        return label;
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    _setDeviceProps(type) {
        let foundRules = {};

        if (type === "then") {
            this._model._currentFilterExpression =
                this._model._parentRenderer._thenAdvancedFilter._renderer.$builderContainer.queryBuilder("getRules");
        } else {
            this._model._currentFilterExpression =
                this._model._parentRenderer._elseAdvancedFilter._renderer.$builderContainer.queryBuilder("getRules");
        }
        foundRules = this.findInFilter(this._model._currentFilterExpression);
        this._DevicePropertiesRegExComponent.setValue(this.findAllProperties(foundRules));
    }

    findAllProperties(values) {
        this._devicePropsData = {};
        values.forEach((val) => {
            const arr = val.match(/(?<=\$\{device.).+?(?=\})/g);
            if (arr) {
                arr.forEach((i) => Object.assign(this._devicePropsData, { [i]: "" }));
            }
        });
        return JSON.stringify(this._devicePropsData, undefined, 4);
    }

    findInFilter(input, filter = []) {
        if (input.rules) {
            input.rules.forEach((currentRule, idx) => {
                if (currentRule.hasOwnProperty("value")) {
                    if (currentRule.value) {
                        currentRule.value = currentRule.value.trimEnd();
                    }
                    input.rules[idx].value = currentRule.value.split("\n");
                    if (currentRule.value) {
                        currentRule.value.forEach((line) => {
                            filter.push(line);
                        });
                    }
                }
                if (currentRule.hasOwnProperty("rules")) {
                    this.findInFilter(currentRule, filter);
                }
            });
        }
        return filter;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    // TODO Private Render Methods here

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onCancelButtonClick() {
        this.model.cancelAction();
    }

    _onSaveButtonClick() {
        this._testButton.setDisabled(true);
        this._testButton.setLabel("Running...");
        this._model._runTest();
    }

    _onTestDataChange() {
        this._resetResults();
        this._model.updateWorkInput();

        if (this._testDataRegExComponent.getValue() !== "") {
            this._testButton.setDisabled(false);
        } else {
            this._testButton.setDisabled(true);
        }
    }

    _onFiltersChange(result) {
        this._setResults(this._evalResults[result]);
    }

    _onConditionsListChange(value) {
        this._setDeviceProps(value);
        this._onTestDataChange();
    }

    _onResults(result) {
        this._testButton.setLabel("Test");
        this._testButton.setDisabled(true);
        this._evalResults = result.summary.evalResults;
        const resultKey = _.keys(this._evalResults);

        if (resultKey.length === 1 && _.includes(["<FullConfig>", "<FilteredConfig>", "<Empty>", "<All>"], resultKey[0])) {
            this._filterList.setOptions([
                {
                    value: resultKey[0],
                    label: FILTER_LABELS[resultKey[0]],
                    disabled: resultKey[0] !== "<FullConfig>"
                }
            ]);
            this._filterList.setSelectedOption(resultKey[0]);
            this._filterList.setDisabled(true);
            this._setResults(this._evalResults[resultKey[0]]);
        } else if (resultKey.length > 0) {
            const options = resultKey.map((item) => ({
                value: item,
                label: item
            }));
            this._filterList.setDisabled(false);
            this._filterList.setOptions(options);
            this._filterList.setSelectedOption(resultKey[0]);
            this._setResults(this._evalResults[resultKey[0]]);
        }
    }

    _setResults(evalResults) {
        const results = evalResults;
        this._filterRegExComponent.setValue(!_.isEmpty(results.configLines) ? results.configLines.join("\n") : "");
        const resHtmlString = new AuditPolicyResultTree(results.evalResult).getTreeHtml();
        this._textAreaControl.setValue(resHtmlString);
    }

    _onError(errorMsg) {
        this._testButton.setLabel("Test");
        this._errorContainer.innerText = errorMsg;
        this._testButton.setDisabled(true);
    }

    _onResize(e) {
        if (typeof e[0] === "object") {
            const parallalElement = e[0].target.parentElement.nextSibling || e[0].target.parentElement.previousSibling,
                nextTextArea = parallalElement.querySelector("textarea");

            if (nextTextArea) {
                nextTextArea.setAttribute("style", `height: ${e[0].target.clientHeight}px`);
            }
        }
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _resetResults() {
        this.clearErrorMessage();
        this._filterList.setDisabled(true);
        this._filterList.setOptions([
            {
                value: "filter",
                label: ""
            }
        ]);
        this._filterList.setValue("filter", false);
        this._filterRegExComponent.setValue("");
        this._textAreaControl.setValue("");
    }

    clearErrorMessage() {
        this._errorContainer.innerText = "";
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = TestRuleRenderer;
